// Event is of type IpcRendererEvent, which doesn't exist in the web app right now, so just use any
import { MessageThread } from './messageThread'
import { PhoneCall, PhoneCallWs } from './phone'
import { ObjectValues } from './utility'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type IpcRendererListener = (event: any, ...args: any[]) => void

type PropagateBrowserChangeFn = <T>(name: string, value: T | null) => void
type RegisterVariableObserverBridgeFn = <T>(
  name: string,
  onChange: (value: T | null) => void,
) => () => void
type GetCurrentValueFn = <T>(name: string) => Promise<T | null>

type VariableObserver = {
  registerVariableObserverBridge: RegisterVariableObserverBridgeFn
  propagateBrowserChange: PropagateBrowserChangeFn
  getCurrentValue: GetCurrentValueFn
}

interface SharedVariableBridge {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue(variableName: string, value: any): void
  subscribe(
    variableName: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    callback: (value: any) => void,
  ): () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue(variableName: string): any
}

interface PopupWindowBridge {
  sendCallPopSetting: (value: string) => void
  sendPopupDimensions: (dimensions: { width: number; height: number }) => void
  setPosition: (position: string) => void
}

interface ClipboardBridge {
  writeText: (text: string) => void
}

export type UpdateOutcomeStatus = 'applied' | 'not_applied'

export type UpdateInstallQuitReason =
  'restart_to_update' | 'channel_switch' | 'quit' | 'session_end'

export type UpdateInstallerResult = 'not_started' | 'started' | 'installed'

export interface UpdateOutcome {
  status: UpdateOutcomeStatus
  fromVersion: string
  toVersion: string
  runningVersion: string
  attemptedAt: string
  /** Optional: main processes before this field send none. */
  quitReason?: UpdateInstallQuitReason | null
  elevationRequired?: boolean | null
  userIsAdmin?: boolean | null
  /** Windows only: how far the installer got, from its status file. */
  installerResult?: UpdateInstallerResult | null
}

export interface UpdateInstallPermissionStatus {
  /** The user can't write to the installed app, so updates would need an admin password. */
  missing: boolean
  helpUrl: string
}

interface UpdatesBridge {
  check: () => void
  /** Checks and installs even though macOS will ask for an administrator password. */
  checkIgnoringInstallPermission: () => void
  quitAndInstall: () => void
  /**
   * Outcome of the most recent install attempt, or null when the last launch
   * did not follow one. Resolves once per app run: the record is consumed on
   * the first call.
   */
  getLastOutcome: () => Promise<UpdateOutcome | null>
  /**
   * Latest result of comparing the app version with the channel's
   * `recommendedVersion` and `minimumVersion`. Null in the popup window, which
   * must never block. Changes are pushed to the main window on
   * `update-requirement`.
   */
  getRequirement: () => Promise<UpdateRequirement | null>
  /**
   * Main window only: native notification, taskbar flash (Windows) and dock
   * bounce (macOS) when the required-update block starts. Once per app run.
   */
  alertRequired: (alert: { title: string; body: string }) => void
  /** Result of the latest update check's permission check; null outside the main window. */
  getInstallPermission: () => Promise<UpdateInstallPermissionStatus | null>
}

interface YealinkDeviceBridge {
  callMuteChanged: (mute: boolean, cid: number) => void
  callHoldChanged: (
    hold: boolean,
    cid: number,
    caller: { name: string; number: string },
  ) => void
  callStarted: (cid: number) => void
  callTerminated: (cid: number) => void
  callRinging: (ringing: boolean, cid: number) => void
  incomingCall: (cid: number, caller: { name: string; number: string }) => void
  outboundCall: (cid: number, caller: { name: string; number: string }) => void
  reset: () => void
}

interface HotKeysBridge {
  sendSetGlobalPhoneHotkey: (keyCombo: string) => void
  sendRemoveGlobalPhoneHotkey: (keyCombo: string) => void
}

interface ChannelsBridge {
  getCurrentChannel: () => Promise<string>
  getBuildEnv: () => Promise<string>
  getAvailableChannels: () => Promise<ChannelConfig[]>
  switchChannel: (
    channel: string,
  ) => Promise<{ success: boolean; needsRestart: boolean }>
  switchChannelWithUpdate: (
    channel: string,
  ) => Promise<{ success: boolean; error?: string }>
  restartApp: () => Promise<void>
  getUpdaterStatus: () => Promise<{
    currentVersion: string
    channel: string
    s3Path: string
    allowDowngrade: boolean
  }>
  validateChannel: (
    channel: string,
  ) => Promise<{ exists: boolean; version: string | null }>
  getChannelVersion: (channel: string) => Promise<string | null>
  getDebugInfo: () => Promise<{
    buildType: string
    currentChannel: string
    lastSwitched: string | null
    wasRecentlySwitched: boolean
  }>
}

export type ChannelRisk = 'low' | 'medium' | 'high'

export interface ChannelConfig {
  name: string
  displayName: string
  internalOnly: boolean
  risk: ChannelRisk
  s3Path: string
  description: string
  recommendedVersion?: string
  /** ISO date shown in the recommended-update banner. Display only. */
  requiredBy?: string
  minimumVersion?: string
}

export type UpdateRequirementLevel = 'none' | 'recommended' | 'required'

export interface UpdateRequirement {
  level: UpdateRequirementLevel
  currentVersion: string
  recommendedVersion: string | null
  requiredBy: string | null
  minimumVersion: string | null
  downloadUrl: string | null
  /** Increases with each result main commits; 0 before the first. */
  revision: number
}

export interface ElectronBridge {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  send: (channel: string, ...args: any[]) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  invoke: (channel: string, ...args: any[]) => Promise<any>
  on: (channel: string, func: IpcRendererListener) => void
  once: (channel: string, func: IpcRendererListener) => void
  removeAllListeners: (channel: string) => void
  removeListener: (channel: string, func: IpcRendererListener) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  receive: (channel: string, func: (...args: any[]) => void) => void
  // Build arch (process.arch), e.g. 'x64' | 'ia32' | 'arm64'. Absent on older binaries.
  arch?: string
  // process.platform, e.g. 'darwin' | 'win32'. Absent on older binaries.
  osPlatform?: string
  // OS version from process.getSystemVersion(): '15.6.1' on macOS,
  // '10.0.22631' on Windows. Absent on older binaries.
  osVersion?: string
  getPartnerId: () => Promise<string | null>
  yealinkDevice: YealinkDeviceBridge
  popupWindow: PopupWindowBridge
  sharedVariables: SharedVariableBridge
  variableObserver: VariableObserver
  clipboard: ClipboardBridge
  updates: UpdatesBridge
  hotKeys: HotKeysBridge
  channels: ChannelsBridge
}

export const PopupQueueItemTypes = {
  CALL: 'call',
  MESSAGE: 'message',
} as const

export type PopupQueueItemType = ObjectValues<typeof PopupQueueItemTypes>

export type PopupQueueItemBase = {
  _id: string
  type: PopupQueueItemType
  createdAt?: Date
}

export type CallQueueItem = Partial<PhoneCall> &
  Partial<PhoneCallWs> &
  PopupQueueItemBase
export type MessageQueueItem = Partial<MessageThread> & PopupQueueItemBase

export type PopupQueueItem = CallQueueItem | MessageQueueItem

export const SharedVariables = {
  CallList: 'CallList',
  MessageQueue: 'MessageQueue',
  ParkingLots: 'ParkingLots',
  HybridCallState: 'HybridCallState',
  SipConnected: 'SipConnected',
  ActiveCallUuid: 'ActiveCallUuid',
  PendingInvitationUuid: 'PendingInvitationUuid',
  IsAtMaxCallLimit: 'IsAtMaxCallLimit',
  CallAnnouncement: 'CallAnnouncement',
  CallListHidesRinging: 'CallListHidesRinging',
} as const

/**
 * A screen reader message the main window hands to the call-pop window,
 * e.g. a failed hold the popup's user wouldn't otherwise hear.
 */
export interface RelayedCallAnnouncement {
  id: string
  message: string
  sentAt: number
}
